import {
  COMMENTING_PROGRESS,
  computeCommentingCommentsProgress,
  isCommentingProgressTool,
  mergeCommentingProgress,
} from "src/components/PersistentChat/commentingProgress";
import type { CommentingData } from "src/stores/persistentChatStore";

export const COMMENTING_PROGRESS_RESET = {
  commentingProgress: 0,
  commentingToolStartedAt: null as number | null,
};

export type CommentingProgressSlice = typeof COMMENTING_PROGRESS_RESET;

type StoreGet = () => {
  activeCommenting: CommentingData | null;
  commentingProgress: number;
};

type StoreSet = (
  partial:
    | Partial<CommentingProgressSlice & { commentingProgress: number }>
    | ((state: StoreGet extends () => infer S ? S : never) => Partial<CommentingProgressSlice & { commentingProgress: number }>),
) => void;

export function isActiveCommentingRun(get: StoreGet): boolean {
  return get().activeCommenting?.status === "in_progress";
}

export function bumpCommentingProgress(
  get: StoreGet,
  set: StoreSet,
  next: number,
  extra?: Partial<CommentingProgressSlice>,
): void {
  if (!isActiveCommentingRun(get)) return;
  set((state) => ({
    commentingProgress: mergeCommentingProgress(state.commentingProgress, next),
    ...extra,
  }));
}

export function commentingProgressFieldsForStarted(): CommentingProgressSlice {
  return {
    commentingProgress: COMMENTING_PROGRESS.started,
    commentingToolStartedAt: null,
  };
}

function isMutateManuscriptCommentsCreate(
  toolName: string,
  toolArgs?: Record<string, unknown>,
): boolean {
  if (toolName !== "mutate_manuscript_comments") return false;
  const action = typeof toolArgs?.action === "string" ? toolArgs.action.trim().toLowerCase() : "create";
  return action === "create";
}

/** Show the commenting card as soon as create-comment tooling starts, not only after the first success. */
export function ensureActiveCommentingSessionOnToolStart(
  get: StoreGet,
  set: StoreSet,
  toolName: string,
  toolArgs?: Record<string, unknown>,
): void {
  if (!isMutateManuscriptCommentsCreate(toolName, toolArgs)) return;
  if (get().activeCommenting?.status === "in_progress") return;

  set({
    activeCommenting: {
      id: `commenting-${Date.now()}`,
      status: "in_progress",
      commentsCreated: 0,
      feedbackTypes: [],
      customFeedbackInstructions: [],
    },
    ...commentingProgressFieldsForStarted(),
  });
}

export function onCommentingToolStart(get: StoreGet, set: StoreSet, toolName: string): void {
  if (!isCommentingProgressTool(toolName)) return;
  set((state) => ({
    commentingProgress: mergeCommentingProgress(
      state.commentingProgress,
      COMMENTING_PROGRESS.toolRunning,
    ),
    commentingToolStartedAt: state.commentingToolStartedAt ?? Date.now(),
  }));
}

export function onCommentingToolComplete(get: StoreGet, set: StoreSet, toolName: string): void {
  if (!isCommentingProgressTool(toolName)) return;
  set((state) => ({
    commentingProgress: mergeCommentingProgress(state.commentingProgress, COMMENTING_PROGRESS.toolComplete),
    commentingToolStartedAt: null,
  }));
}

export function onCommentingCommentCreated(get: StoreGet, set: StoreSet, totalCreated: number): void {
  if (!isActiveCommentingRun(get)) return;
  const feedbackTypeCount = get().activeCommenting?.feedbackTypes.length ?? 0;
  bumpCommentingProgress(
    get,
    set,
    computeCommentingCommentsProgress(totalCreated, feedbackTypeCount),
    { commentingToolStartedAt: null },
  );
}

export function onCommentingFinished(get: StoreGet, set: StoreSet): void {
  if (!get().activeCommenting) return;
  bumpCommentingProgress(get, set, COMMENTING_PROGRESS.done);
}
